"use client";

import {
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Grid,
  Drawer,
  IconButton,
  Collapse,
  Link,
} from "@mui/material";
import { pxToVw } from "@/utils/pxToVw";
import { MOBILE_BASE } from "@/theme/theme";
import { useState } from "react";
import { ArxiusItem } from "@/types/arxius";
import React from "react";
import TextWrapAround from "./TextWrapAround";
import Image from "next/image";
import { useText } from "@/context/TextContext";

type Props = {
  arxius: ArxiusItem[];
};

export default function ArxiuHistoricSection({ arxius }: Props) {
  const [expandedBlocks, setExpandedBlocks] = useState<{
    [key: number]: boolean;
  }>({});
  const [selected, setSelected] = useState<{
    arxiuIndex: number;
    aconIndex: number;
  } | null>(null);

  const handleToggle = (index: number) => {
    setExpandedBlocks((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // --- 🔑 Crear lista plana de todos los acontecimientos ---
  const flatList = arxius.flatMap((arxiu, arxiuIndex) =>
    arxiu.aconteixements.map((acon, aconIndex) => ({
      arxiuIndex,
      aconIndex,
      any: arxiu.any,
      ...acon,
    }))
  );

  const currentIndex = selected
    ? flatList.findIndex(
      (item) =>
        item.arxiuIndex === selected.arxiuIndex &&
        item.aconIndex === selected.aconIndex
    )
    : -1;

  const handlePrev = () => {
    if (currentIndex > 0) {
      const prev = flatList[currentIndex - 1];
      setSelected({ arxiuIndex: prev.arxiuIndex, aconIndex: prev.aconIndex });
    }
  };

  const handleNext = () => {
    if (currentIndex < flatList.length - 1) {
      const next = flatList[currentIndex + 1];
      setSelected({ arxiuIndex: next.arxiuIndex, aconIndex: next.aconIndex });
    }
  };

  return (
    <React.Fragment>
      <Box
        pt={{ xs: pxToVw(20, MOBILE_BASE), md: pxToVw(0) }}
        pb={{ xs: pxToVw(80, MOBILE_BASE), md: pxToVw(160) }}
        px={{ xs: pxToVw(15, MOBILE_BASE), md: pxToVw(21) }}
      >
        <Box mb={{ xs: pxToVw(80, MOBILE_BASE), md: pxToVw(160) }}>
          <TextWrapAround title="" text={useText("arxiu.main.text")} />
        </Box>
        <Typography
          variant="titulo"
          color="marron"
          component="h1"
          mb={{ xs: pxToVw(15, MOBILE_BASE), md: pxToVw(15) }}
          sx={{ textTransform: "uppercase" }}
        >
          {useText("header.arxiu.title")}
        </Typography>
        {/* --- Desktop --- */}
        {!isMobile && (
          <Grid
            container
            spacing={{ xs: pxToVw(20, MOBILE_BASE), md: pxToVw(8) }}
          >
            {arxius.map((arxiu, index) => (
              <Grid
                container
                key={index}
                size={{ xs: 12, md: 4 }}
                borderTop={{ xs: "none", md: "1px solid #472600" }}
                pt={pxToVw(10)}
                mb={{ xs: pxToVw(20, MOBILE_BASE), md: pxToVw(100) }}
                alignContent={"flex-start"}
              >
                <Typography
                  variant="body1"
                  color="marron"
                  component="h2"
                  pb={{ xs: pxToVw(20, MOBILE_BASE), md: pxToVw(72) }}
                >
                  {arxiu.any}
                </Typography>
                {arxiu.aconteixements?.map((aconteixement, indexAcon) => (
                  <Grid
                    container
                    size={{ xs: 12, md: 12 }}
                    spacing={0}
                    direction="row"
                    key={indexAcon}
                    pb={{ xs: pxToVw(50, MOBILE_BASE), md: pxToVw(50) }}
                  >
                    <Grid
                      container
                      size={{ xs: 12, md: 3 }}
                      key={`data-${indexAcon}`}
                      borderTop={{ xs: "none", md: "1px solid #472600" }}
                      pt={{ xs: pxToVw(5, MOBILE_BASE), md: pxToVw(5) }}
                    >
                      <Typography
                        variant="body4"
                        color="marron"
                        sx={{
                          cursor: "pointer",
                          ":hover": {
                            color: "#9B8D82",
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected({
                            arxiuIndex: index,
                            aconIndex: indexAcon,
                          });
                        }}
                        dangerouslySetInnerHTML={{ __html: aconteixement.data }}
                      />
                    </Grid>
                    <Grid
                      container
                      size={{ xs: 12, md: 9 }}
                      key={`descripcio-${indexAcon}`}
                      borderTop={{ xs: "none", md: "1px solid #472600" }}
                      pt={{ xs: pxToVw(5, MOBILE_BASE), md: pxToVw(5) }}
                      pr={{ xs: 0, md: pxToVw(13) }}
                      sx={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected({
                          arxiuIndex: index,
                          aconIndex: indexAcon,
                        });
                      }}
                    >
                      <Typography
                        variant="body4"
                        color="marron"
                        sx={{
                          ":hover": {
                            color: "#9B8D82",
                          },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: aconteixement.descripcio,
                        }}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            ))}
          </Grid>
        )}

        {/* --- Mobile --- */}
        {isMobile && (
          <Grid
            size={12}
            container
            columns={{ xs: 12 }}
            spacing={{ xs: pxToVw(80, MOBILE_BASE) }}
            pb={{ xs: pxToVw(50, MOBILE_BASE) }}
            borderBottom={"1px solid #472600"}
          >
            {arxius.map((arxiu, index) => {
              const isExpanded = !!expandedBlocks[index];
              return (
                <Grid
                  key={index}
                  size={12}
                  container
                  paddingTop={{ xs: pxToVw(6, MOBILE_BASE) }}
                  borderTop={"1px solid #472600"}
                  spacing={{ xs: pxToVw(3, MOBILE_BASE) }}
                  sx={{ cursor: "pointer" }}
                >
                  {/* Año */}
                  <Grid
                    size={{ xs: 10 }}
                    container
                    onClick={() => handleToggle(index)}
                  >
                    <Typography
                      variant="label1"
                      fontSize={{ xs: pxToVw(48, MOBILE_BASE) }}
                      lineHeight={{ xs: "100%" }}
                      color="marron"
                    >
                      {arxiu.any}
                    </Typography>
                  </Grid>

                  {/* Flecha */}
                  <Grid
                    size={{ xs: 2 }}
                    container
                    justifyContent="flex-end"
                    onClick={() => handleToggle(index)}
                  >
                    <Typography variant="body1" color="marron">
                      {isExpanded ? "↑" : "↓"}
                    </Typography>
                  </Grid>

                  {/* Contenido expandido */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    {arxiu.aconteixements?.map((aconteixement, indexAcon) => (
                      <Grid
                        container
                        key={`aconteixement-${indexAcon}`}
                        mb={
                          indexAcon === arxiu.aconteixements.length - 1
                            ? 0
                            : pxToVw(47, MOBILE_BASE)
                        }
                        mt={indexAcon === 0 ? pxToVw(50, MOBILE_BASE) : 0}
                        size={{ xs: 12 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected({
                            arxiuIndex: index,
                            aconIndex: indexAcon,
                          });
                        }}
                      >
                        <Grid
                          container
                          size={{ xs: 3 }}
                          key={`data-${indexAcon}`}
                          borderTop={{ xs: "1px solid #472600" }}
                          pt={{ xs: pxToVw(6, MOBILE_BASE) }}
                        >
                          <Typography
                            variant="body3"
                            color="marron"
                            dangerouslySetInnerHTML={{
                              __html: aconteixement.data,
                            }}
                          />
                        </Grid>
                        <Grid
                          container
                          size={{ xs: 9 }}
                          borderTop={{ xs: "1px solid #472600" }}
                          pt={{ xs: pxToVw(6, MOBILE_BASE) }}
                        >
                          <Grid
                            container
                            size={{ xs: 11 }}
                            key={`descripcio-${indexAcon}`}
                          >
                            <Typography
                              variant="body3"
                              color="marron"
                              dangerouslySetInnerHTML={{
                                __html: aconteixement.descripcio,
                              }}
                            />
                          </Grid>
                          <Grid
                            size={{ xs: 1 }}
                            container
                            justifyContent="flex-end"
                          >
                            <Typography variant="body3" color="marron">
                              +
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    ))}
                  </Collapse>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <Drawer
        anchor="right"
        open={selected !== null}
        onClose={() => setSelected(null)}
        hideBackdrop
        slotProps={{
          paper: {
            sx: {
              width: { xs: "100%", md: pxToVw(600) },
              bgcolor: "#D4D1C8",
              padding: { xs: pxToVw(15, MOBILE_BASE), md: pxToVw(22) },
            },
          },
        }}
      >
        {selected && currentIndex !== -1 && (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={{ xs: pxToVw(60, MOBILE_BASE), md: pxToVw(40) }}
            >
              <IconButton
                aria-label="anterior"
                disabled={currentIndex <= 0}
                onClick={handlePrev}
                sx={{
                  fontSize: { xs: pxToVw(33, MOBILE_BASE), md: pxToVw(20) },
                  lineHeight: { xs: "110%", md: "105%" },
                  color: "#472600",
                  padding: 0,
                  fontFamily: "RomekSharp",
                  ":hover": { backgroundColor: "transparent" },
                }}
              >
                ←
              </IconButton>
              <Typography
                variant="body1"
                color="marron"
                sx={{
                  fontSize: { xs: pxToVw(48, MOBILE_BASE), md: pxToVw(48) },
                  lineHeight: { xs: "100%", md: "110%" },
                }}
              >
                {flatList[currentIndex].any}
              </Typography>
              <IconButton
                aria-label="siguiente"
                disabled={currentIndex >= flatList.length - 1}
                onClick={handleNext}
                sx={{
                  fontSize: { xs: pxToVw(33, MOBILE_BASE), md: pxToVw(20) },
                  lineHeight: { xs: "110%", md: "105%" },
                  color: "#472600",
                  padding: 0,
                  fontFamily: "RomekSharp",
                  ":hover": { backgroundColor: "transparent" },
                }}
              >
                →
              </IconButton>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              textAlign={"center"}
              flexDirection={"column"}
              mx={{ xs: pxToVw(48, MOBILE_BASE), md: pxToVw(120) }}
            >
              {/* Contenido */}
              <Typography
                variant="body4"
                color="marron"
                sx={{
                  fontSize: { xs: pxToVw(22, MOBILE_BASE), md: pxToVw(20) },
                  lineHeight: { xs: "110%", md: "105%" },
                }}
                dangerouslySetInnerHTML={{
                  __html: flatList[currentIndex].descripcio,
                }}
              />
              {/* Imagenes */}
              {flatList[currentIndex].images &&
                flatList[currentIndex].images.length > 0 && (
                  <Box
                    mt={{ xs: pxToVw(60, MOBILE_BASE), md: pxToVw(40) }}
                    width={"100%"}
                  >
                    {flatList[currentIndex].images.map((img, idx) => (
                      <Box
                        key={idx}
                        display="flex"
                        flexDirection="column"
                        mb={{ xs: pxToVw(7, MOBILE_BASE), md: pxToVw(7) }}
                      >
                        {/*<Image
                          alt={img.alt || img.src}
                          src={img.src}
                          width={img.width}
                          height={img.height}
                          style={{ width: "100%", height: "auto" }}
                        />*/}
                        {/* If enlaceImagen is not null, put Image inside Link */}
                        {flatList[currentIndex].enlaceImagen ? (
                          <Link
                            href={flatList[currentIndex].enlaceImagen!}
                            target="_blank"
                            rel="noopener noreferrer"
                          ><Image
                              alt={img.alt || img.src}
                              src={img.src}
                              width={img.width}
                              height={img.height}
                              style={{ width: "100%", height: "auto" }}
                            /></Link>
                        ) : (
                          <Image
                            alt={img.alt || img.src}
                            src={img.src}
                            width={img.width}
                            height={img.height}
                            style={{ width: "100%", height: "auto" }}
                          />
                        )}
                        {img.caption && (
                          <Typography
                            color="warmaa"
                            textAlign={"left"}
                            mt={{ xs: pxToVw(7, MOBILE_BASE), md: pxToVw(7) }}
                            fontSize={{
                              xs: pxToVw(12, MOBILE_BASE),
                              md: pxToVw(12),
                            }}
                            lineHeight={{
                              xs: pxToVw(16, MOBILE_BASE),
                              md: pxToVw(16),
                            }}
                          >
                            {img.caption}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
            </Box>
            {/* Botón cerrar */}

            <IconButton
              onClick={() => setSelected(null)}
              sx={{
                position: "fixed",
                right: { xs: pxToVw(20, MOBILE_BASE), md: pxToVw(20) },
                bottom: { xs: pxToVw(20, MOBILE_BASE), md: pxToVw(20) },
                fontSize: { xs: pxToVw(33, MOBILE_BASE), md: pxToVw(20) },
                lineHeight: 1,
                color: "#472600",
                padding: 0,
                fontFamily: "RomekSharp",
                ":hover": { backgroundColor: "transparent" },
              }}
            >
              ✕
            </IconButton>
          </>
        )}
      </Drawer>
    </React.Fragment>
  );
}
